import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { APRYZE_STATIC_URL } from '@smx-ui-common/consts';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { SmxPermissionContext } from '@smx-ui-permission/provider/permission-provider';
import { PermissionCombinationType } from '@smx-ui-permission/types';
import { Spin } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { API } from '../../../../service/drawings/src/ui/common';
import { UI_ELEMENTS } from './constants/ui-elements';

// Global suppression of Apryse errors (kept to match existing viewer behavior).
if (typeof window !== 'undefined') {
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
        const message = args[0]?.toString() || '';
        const stack = args[0]?.stack || '';
        if (
            message.includes('exposedSelectors') ||
            message.includes('Cannot read properties of undefined') ||
            message.includes('webviewer-core') ||
            stack.includes('webviewer-core') ||
            stack.includes('exposedSelectors')
        ) {
            return;
        }
        originalConsoleError.apply(console, args);
    };

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const message = event.reason?.message || event.reason?.toString() || '';
        const stack = event.reason?.stack || '';
        if (
            message.includes('Cannot read properties of undefined') ||
            message.includes('forEach') ||
            stack.includes('webviewer-core') ||
            stack.includes('exposedSelectors')
        ) {
            event.preventDefault();
            event.stopPropagation();
        }
    });

    window.addEventListener('error', (event: ErrorEvent) => {
        const message = event.message || '';
        const filename = event.filename || '';
        if (
            message.includes('Cannot read properties of undefined') ||
            filename.includes('webviewer-core') ||
            filename.includes('exposedSelectors')
        ) {
            event.preventDefault();
            event.stopPropagation();
        }
    });
}

export type ViewerAppryzeContextType = 'drawing' | 'document' | 'attachmentScheduleItem' | 'attachmentTask';

export type ViewerAppryzeLiteProps = {
    file: string | File | null;
    fileName?: string;
    height?: string;
    toolBar?: (instance: WebViewerInstance) => void;
    displaySpinner?: boolean;
    context: ViewerAppryzeContextType;
};

const getPermissionsList = (context: ViewerAppryzeContextType): PermissionCombinationType[] => {
    switch (context) {
        case 'drawing':
            return ['drawing.manage'];
        case 'document':
            return ['documents.manage'];
        case 'attachmentScheduleItem':
            return ['work-item.manage'];
        case 'attachmentTask':
            return ['schedule-task.manage'];
        default:
            return [];
    }
};

const TYPE_IMAGE = 'image';

export const ViewerAppryzeLite: React.FC<ViewerAppryzeLiteProps> = ({
    file,
    fileName,
    toolBar,
    height = '186px',
    context,
    displaySpinner = false,
}) => {
    const webViewerRef = useRef<HTMLDivElement>(null);
    const isViewerInitialized = useRef<boolean>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [webViewer, setWebViewer] = useState<WebViewerInstance | null>(null);
    const intervalValueRef = useRef<number | NodeJS.Timeout>(0);
    const autoSwitchRestoreRef = useRef<boolean | null>(null);
    const fileRef = useRef(file);
    const fileNameRef = useRef(fileName);
    useEffect(() => {
        fileRef.current = file;
        fileNameRef.current = fileName;
    }, [file, fileName]);

    const { displayName } = useContext(SmxPermissionContext);
    const permission = getPermissionsList(context);
    const accessManager = useAccess(permission);
    const fullDomain = window.location.origin;

    useEffect(() => {
        let instance: WebViewerInstance | null = null;
        let cleanupDocumentLoaded: (() => void) | null = null;

        const initWebViewer = async () => {
            if (!webViewerRef.current) {
                return;
            }

            try {
                instance = await WebViewer.WebComponent(
                    {
                        licenseKey: API.APRYSE_LICENSE,
                        path: APRYZE_STATIC_URL,
                        isAdminUser: accessManager,
                        annotationUser: displayName,
                        enableAnnotations: false,
                        enableMeasurement: true,
                        css: accessManager ? `${fullDomain}/style-manager.css` : `${fullDomain}/style.css`,
                    },
                    webViewerRef.current,
                );

                instance.UI.enableFeatures([instance.UI.Feature.Download]);
                instance.UI.enableElements(['downloadButton', 'downloadPresetButton']);

                instance.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);

                const { documentViewer } = instance.Core;
                const onDocumentLoaded = () => {
                    const document = documentViewer.getDocument();
                    if (document && document.getType() === TYPE_IMAGE && instance) {
                        const downloadOriginalImage = () => {
                            const currentFile = fileRef.current;
                            if (!currentFile) return;
                            const inferredName =
                                (currentFile instanceof File ? currentFile.name : undefined) ||
                                fileNameRef.current ||
                                'image';
                            const anchor = window.document.createElement('a');
                            let revokeUrl: string | null = null;
                            if (typeof currentFile === 'string') {
                                anchor.href = currentFile;
                            } else if (currentFile instanceof Blob) {
                                revokeUrl = URL.createObjectURL(currentFile);
                                anchor.href = revokeUrl;
                            } else {
                                return;
                            }
                            anchor.download = inferredName;
                            anchor.style.display = 'none';
                            window.document.body.appendChild(anchor);
                            anchor.click();
                            window.document.body.removeChild(anchor);
                            if (revokeUrl) {
                                setTimeout(() => URL.revokeObjectURL(revokeUrl!), 0);
                            }
                        };

                        instance.UI.disableFeatures([instance.UI.Feature.Download]);
                        instance.UI.disableElements(['downloadButton', 'downloadPresetButton']);

                        try {
                            const flyout = instance.UI.Flyouts?.getFlyout?.('MainMenuFlyout');
                            if (flyout) {
                                const customItem = {
                                    dataElement: 'smxImageDownload',
                                    label: 'action.download',
                                    icon: 'icon-download',
                                    onClick: downloadOriginalImage,
                                };
                                const items = ((flyout.items as any[]) || []).filter(
                                    (it: any) => it?.dataElement !== 'smxImageDownload',
                                );
                                items.push(customItem);
                                flyout.setItems(items);
                            }
                        } catch {
                            // If the Flyouts API isn't available in this Apryse build, the
                            // disableFeatures call above still ensures no stale Download remains.
                        }
                    }
                    if (context === 'drawing') {
                        try {
                            const panTool = documentViewer.getTool('Pan');
                            if (panTool) {
                                documentViewer.setToolMode(panTool);
                            }
                        } catch {
                            // ignore tool mode errors
                        }
                    }
                };
                documentViewer.addEventListener('documentLoaded', onDocumentLoaded);
                cleanupDocumentLoaded = () => {
                    documentViewer.removeEventListener('documentLoaded', onDocumentLoaded);
                };

                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL]);
                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL_HEADER]);

                setWebViewer(instance);
            } catch (error) {
                console.error('Error initializing WebViewer:', error);
            }
        };

        if (!isViewerInitialized.current) {
            isViewerInitialized.current = true;
            initWebViewer();
        }

        return () => {
            cleanupDocumentLoaded?.();
            if (instance) {
                instance.UI.closeDocument();
                setWebViewer(null);
                isViewerInitialized.current = false;
            }
        };
    }, []);

    useEffect(() => {
        if (!webViewer || context !== 'drawing') return;

        const tool = webViewer.Core?.Tools?.Tool;
        if (!tool) return;

        if (autoSwitchRestoreRef.current === null) {
            autoSwitchRestoreRef.current = tool.ENABLE_AUTO_SWITCH;
        }

        tool.disableAutoSwitch();

        return () => {
            if (autoSwitchRestoreRef.current === null) return;
            if (autoSwitchRestoreRef.current) {
                tool.enableAutoSwitch();
            } else {
                tool.disableAutoSwitch();
            }
        };
    }, [webViewer, context]);

    useEffect(() => {
        if (!webViewer) return;

        if (toolBar) {
            toolBar(webViewer);
        } else {
            webViewer.UI.disableElements([UI_ELEMENTS.TOOLS_HEADER]);
        }

        webViewer.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);
    }, [webViewer, toolBar]);

    useEffect(() => {
        if (!webViewer || !file) return;

        setLoading(true);

        try {
            webViewer.UI.loadDocument(file, {
                filename: (file as File)?.name || fileName,
            });

            intervalValueRef.current = setInterval(() => {
                if (webViewer.Core.documentViewer.getDocument()) {
                    clearInterval(intervalValueRef.current);
                    setLoading(false);
                }
            }, 200);
        } catch (error) {
            console.error('Error loading document:', error);
        }

        return () => {
            clearInterval(intervalValueRef.current);
        };
    }, [webViewer, file, fileName]);

    return (
        <>
            <div
                ref={webViewerRef}
                style={{
                    width: '100%',
                    height,
                }}
            />
            {displaySpinner && <Spin spinning={loading} fullscreen={true}></Spin>}
        </>
    );
};
